import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import AccountDetail from './AccountDetail';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';
import SlackChannelsPicker, { channelsFromTemplates, defaultMembers } from '../components/SlackChannelsPicker';

const STATUS_MAP = {
  active: { label: 'Aktif', color: '#34D399' },
  paused: { label: 'Duraklatılmış', color: '#F2B84B' },
  churned: { label: 'Churned', color: '#FF6B5B' },
  inactive: { label: 'Pasif', color: '#74746D' },
  internal: { label: 'İç grup', color: '#7AA2F7' },
};

const ONBOARDING_STATUS = {
  pending: { label: 'Bekliyor', color: '#9A9A92' },
  in_progress: { label: 'Devam Ediyor', color: '#D4FF3A' },
  emails_sent: { label: 'Mailler Gönderildi', color: '#A78BFA' },
  completed: { label: 'Tamamlandı', color: '#34D399' },
};

const emptyForm = {
  first_name: '',
  company_name: '',
  brand_name: '',
  sellerboard_email: '',
  sales_id: '',
  am_id: '',
  ppc_id: '',
};

const emptyOnboarding = {
  language: 'TR',
  subject_topic: '',
  company_setup: false,
  cpa_firm: 'none',
  mercury_setup: false,
  incentive: false,
};

export default function Accounts() {
  const { can } = useAuth();
  const canBilling = can('billing.view');
  const canDeleteAccount = can('accounts.delete');
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [contacts, setContacts] = useState([{ name: '', email: '', role: '', is_primary: true }]);
  const [onboarding, setOnboarding] = useState(emptyOnboarding);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Yeni markaya açılacak Slack kanalları (şablonlar Ayarlar'dan yönetilir)
  const [channelTemplates, setChannelTemplates] = useState([]);
  const [slackChannels, setSlackChannels] = useState([]);
  useEffect(() => {
    api.get('/slack-channel-templates').then(r => setChannelTemplates(r.data)).catch(() => {});
  }, []);
  // "Tüm ekip" = Slack ID'si olan aktif kullanıcılar
  const slackTeam = users.filter(u => u.is_active !== false && u.slack_user_id && u.slack_auto_invite !== false).map(u => u.id);
  // Form açılınca şablonlardan başla
  useEffect(() => {
    if (showForm) setSlackChannels(channelsFromTemplates(channelTemplates, form, slackTeam));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, channelTemplates, users]);
  // Atanan AM / PPC / Satışçı değişince elle değiştirilmemiş kanalların üyelerini güncelle
  useEffect(() => {
    setSlackChannels(list => list.map(c => (c.touched || !c.tpl ? c : { ...c, member_user_ids: defaultMembers(c.tpl, form, slackTeam) })));
  }, [form.am_id, form.ppc_id, form.sales_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async (acc, e) => {
    e.stopPropagation();
    if (!canDeleteAccount) return;
    const confirmText = `"${acc.company_name}" hesabı silinecek. Fatura, ciro, toplantı veya görev geçmişi olan hesaplar silinmez; onları "Pasif" yapın.\n\nDevam edilsin mi?`;
    if (!window.confirm(confirmText)) return;
    setDeletingId(acc.id);
    try {
      await api.delete(`/accounts/${acc.id}`);
      await fetchAccounts();
    } catch (err) {
      // açıklama (ör. "geçmişi var, Pasif yapın") api katmanında gösterilir
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchUsers();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/accounts');
      setAccounts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const addContactRow = () => setContacts([...contacts, { name: '', email: '', role: '', is_primary: false }]);
  const removeContactRow = (idx) => setContacts(contacts.filter((_, i) => i !== idx));
  const updateContact = (idx, key, value) => {
    const next = [...contacts];
    next[idx] = { ...next[idx], [key]: value };
    if (key === 'is_primary' && value) {
      next.forEach((c, i) => { if (i !== idx) c.is_primary = false; });
    }
    setContacts(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const cleanContacts = contacts.filter(c => c.name && c.email);
      if (cleanContacts.length === 0) {
        alert('En az bir kontak gerekli');
        setSubmitting(false);
        return;
      }
      if (!cleanContacts.some(c => c.is_primary)) cleanContacts[0].is_primary = true;

      const payload = {
        ...form,
        sales_id: form.sales_id || null,
        am_id: form.am_id || null,
        ppc_id: form.ppc_id || null,
        contacts: cleanContacts,
        onboarding: { ...onboarding, cpa_firm: onboarding.company_setup ? onboarding.cpa_firm : 'none' },
        slack_channels: slackChannels.map(c => ({ suffix: c.suffix, label: c.label, member_user_ids: c.member_user_ids })),
      };
      await api.post('/accounts', payload);
      setForm(emptyForm);
      setContacts([{ name: '', email: '', role: '', is_primary: true }]);
      setOnboarding(emptyOnboarding);
      setShowForm(false);
      fetchAccounts();
    } catch (err) {
      console.error(err);
      alert('Hesap oluşturulamadı: ' + (err.response?.data?.detail || err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = accounts.filter(a =>
    a.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.brand_name?.toLowerCase().includes(search.toLowerCase()) ||
    a.am_name?.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = accounts.filter(a => a.status === 'active').length;
  const onboardingActiveCount = accounts.filter(a => ['pending', 'in_progress', 'emails_sent'].includes(a.onboarding_status)).length;
  // Aktif ama Stripe Customer ID'si girilmemiş hesaplar (fatura öncesi eşleştirilmeli)
  const stripeMissingCount = accounts.filter(a => a.status === 'active' && !a.stripe_customer_id).length;
  // MRR: aktif hesapların bu ay tahmini final ücretleri toplamı (backend SQL'den geliyor)
  const totalMRR = accounts.filter(a => a.status === 'active').reduce((sum, a) => sum + (parseFloat(a.current_month_fee) || 0), 0);
  const formatCurrency = (val) => !val && val !== 0 ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Hesaplar</h1>
          <p className="page-subtitle">Aktif müşteri hesapları ve onboarding süreçleri</p>
        </div>
        <div className="page-actions">
          <div className="search-bar">
            <span className="search-bar__icon"><Icon name="search" size={15} /></span>
            <input className="search-bar__input" placeholder="Hesap ara..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn btn--primary" onClick={() => setShowForm(true)}>+ Yeni Hesap</button>
        </div>
      </div>

      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-card__header">
            <span className="stat-card__label">Aktif Hesap</span>
            <span className="stat-card__icon"><Icon name="building" size={20} /></span>
          </div>
          <div className="stat-card__value">{activeCount}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__header">
            <span className="stat-card__label">Onboarding Sürecinde</span>
            <span className="stat-card__icon"><Icon name="rocket" size={20} /></span>
          </div>
          <div className="stat-card__value">{onboardingActiveCount}</div>
        </div>
        {canBilling && (
          <div className="stat-card">
            <div className="stat-card__header">
              <span className="stat-card__label">Stripe Eşleşmesi Eksik</span>
              <span className="stat-card__icon"><Icon name="card" size={20} /></span>
            </div>
            <div className="stat-card__value" style={{ color: stripeMissingCount ? '#F2B84B' : undefined }}>{stripeMissingCount}</div>
          </div>
        )}
        {canBilling && (
          <div className="stat-card">
            <div className="stat-card__header">
              <span className="stat-card__label">Toplam MRR</span>
              <span className="stat-card__icon"><Icon name="cash" size={20} /></span>
            </div>
            <div className="stat-card__value">{formatCurrency(totalMRR)}</div>
          </div>
        )}
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div className="page-loading">Yükleniyor...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Şirket</th>
                <th>Marka</th>
                <th>İletişim</th>
                <th>Account Manager</th>
                {canBilling && <th>Aylık Ücret (MTD)</th>}
                <th>Onboarding</th>
                <th>Durum</th>
                {canBilling && <th title="Stripe Customer ID">Stripe</th>}
                {canDeleteAccount && <th style={{ width: 60 }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(acc => (
                <tr key={acc.id} className="data-table__row--clickable" onClick={() => setSelectedId(acc.id)}>
                  <td style={{ fontWeight: 600 }}>{acc.company_name}</td>
                  <td>{acc.brand_name || '—'}</td>
                  <td>{acc.first_name || '—'}</td>
                  <td>{acc.am_name || '—'}</td>
                  {canBilling && (
                    <td style={{ fontWeight: 600, color: parseFloat(acc.current_month_revenue) >= parseFloat(acc.revenue_threshold) ? '#34D399' : '#C9C7BE' }}>
                      {formatCurrency(parseFloat(acc.current_month_fee) || 0)}
                    </td>
                  )}
                  <td>
                    <span className="badge" style={{
                      background: ONBOARDING_STATUS[acc.onboarding_status]?.color + '22',
                      color: ONBOARDING_STATUS[acc.onboarding_status]?.color,
                    }}>
                      {ONBOARDING_STATUS[acc.onboarding_status]?.label || acc.onboarding_status}
                    </span>
                  </td>
                  <td>
                    <span className="badge" style={{
                      background: STATUS_MAP[acc.status]?.color + '22',
                      color: STATUS_MAP[acc.status]?.color,
                    }}>
                      {STATUS_MAP[acc.status]?.label || acc.status}
                    </span>
                  </td>
                  {canBilling && (
                    <td title={acc.stripe_customer_id || 'Stripe Customer ID girilmemiş'}
                      style={{ color: acc.stripe_customer_id ? '#34D399' : '#F2B84B', fontWeight: 600 }}>
                      {acc.stripe_customer_id ? '✓' : '✗'}
                    </td>
                  )}
                  {canDeleteAccount && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => handleDelete(acc, e)}
                        disabled={deletingId === acc.id}
                        title="Hesabı sil (admin)"
                        style={{
                          background: 'transparent', border: '1px solid #FF6B5B55',
                          color: '#FF6B5B', borderRadius: 6,
                          padding: '4px 10px', cursor: deletingId === acc.id ? 'wait' : 'pointer',
                          fontSize: 13,
                        }}
                      >
                        {deletingId === acc.id ? '...' : <Icon name="trash" size={15} />}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6 + (canDeleteAccount ? 1 : 0) + (canBilling ? 2 : 0)} style={{ textAlign: 'center', padding: 32 }} className="text-muted">Hesap bulunamadı</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selectedId && (
        <AccountDetail
          accountId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={fetchAccounts}
        />
      )}

      {/* New Account Form */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal modal--large" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="modal__header">
              <h2 className="modal__title">Yeni Hesap & Onboarding Başlat</h2>
              <button className="modal__close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Temel Bilgiler */}
              <h3 style={{ color: '#C9C7BE', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Temel Bilgiler</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">İsim *</label>
                  <input className="form-input" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Şirket Adı *</label>
                  <input className="form-input" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} required />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Marka Adı</label>
                <input className="form-input" value={form.brand_name} onChange={e => setForm({ ...form, brand_name: e.target.value })} />
              </div>

              <div className="form-group">
                <label className="form-label">Sellerboard E-mail</label>
                <input className="form-input" type="email" value={form.sellerboard_email} onChange={e => setForm({ ...form, sellerboard_email: e.target.value })} />
              </div>

              {/* Ekip */}
              <h3 style={{ color: '#C9C7BE', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, marginTop: 16 }}>Ekip Ataması</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Satışcı</label>
                  <select className="form-input" value={form.sales_id} onChange={e => setForm({ ...form, sales_id: e.target.value })}>
                    <option value="">Seçiniz</option>
                    {users.filter(u => u.role === 'sales' || u.role === 'admin').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Account Manager</label>
                  <select className="form-input" value={form.am_id} onChange={e => setForm({ ...form, am_id: e.target.value })}>
                    <option value="">Seçiniz</option>
                    {users.filter(u => u.role === 'account_manager' || u.role === 'admin').map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">PPC Manager</label>
                  <select className="form-input" value={form.ppc_id} onChange={e => setForm({ ...form, ppc_id: e.target.value })}>
                    <option value="">Seçiniz</option>
                    {users.filter(u => ['ppc_manager', 'account_manager', 'admin'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Slack kanalları */}
              <h3 style={{ color: '#C9C7BE', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, marginTop: 16 }}>Slack Kanalları</h3>
              <p className="text-muted" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
                Kaydedince bu kanallar açılır ve seçilen kişiler davet edilir. Varsayılan üyeler Ayarlar → Slack kanalları'ndan değiştirilir.
              </p>
              <SlackChannelsPicker brand={form.brand_name || form.company_name} users={users.filter(u => u.is_active !== false)}
                channels={slackChannels} onChange={setSlackChannels} />

              {/* Kontaklar */}
              <h3 style={{ color: '#C9C7BE', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, marginTop: 16 }}>Kontaklar (Mail Gönderilecek Kişiler)</h3>

              {contacts.map((c, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto auto', gap: 8, marginBottom: 8 }}>
                  <input className="form-input" placeholder="İsim" value={c.name} onChange={e => updateContact(idx, 'name', e.target.value)} />
                  <input className="form-input" type="email" placeholder="E-mail" value={c.email} onChange={e => updateContact(idx, 'email', e.target.value)} />
                  <input className="form-input" placeholder="Rol (Sahip, Finans...)" value={c.role} onChange={e => updateContact(idx, 'role', e.target.value)} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#9A9A92', fontSize: 12 }}>
                    <input type="radio" name="primary_contact" checked={c.is_primary} onChange={() => updateContact(idx, 'is_primary', true)} />
                    1.
                  </label>
                  {contacts.length > 1 && (
                    <button type="button" onClick={() => removeContactRow(idx)} style={{ background: 'none', border: 'none', color: '#FF6B5B', cursor: 'pointer', fontSize: 18 }}>×</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn btn--secondary" style={{ width: '100%', marginBottom: 16 }} onClick={addContactRow}>+ Kontak Ekle</button>

              {/* Onboarding Checklist */}
              <h3 style={{ color: '#C9C7BE', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, marginTop: 16 }}>Onboarding Checklist</h3>

              <div className="form-group">
                <label className="form-label">Mail Konu Başlığı</label>
                <input className="form-input" placeholder="Örn: Yeni Başarı Hikayeniz İçin Start Veriyoruz" value={onboarding.subject_topic} onChange={e => setOnboarding({ ...onboarding, subject_topic: e.target.value })} />
              </div>

              <div className="form-group">
                <label className="form-label">Mail Dili</label>
                <select className="form-input" value={onboarding.language} onChange={e => setOnboarding({ ...onboarding, language: e.target.value })}>
                  <option value="TR">Türkçe</option>
                  <option value="EN">English</option>
                </select>
              </div>

              <ChecklistToggle
                label="Şirket Kurulacak mı?"
                value={onboarding.company_setup}
                onChange={v => setOnboarding({ ...onboarding, company_setup: v, cpa_firm: v ? onboarding.cpa_firm : 'none' })}
              />

              {onboarding.company_setup && (
                <div className="form-group">
                  <label className="form-label">CPA Firması</label>
                  <select className="form-input" value={onboarding.cpa_firm} onChange={e => setOnboarding({ ...onboarding, cpa_firm: e.target.value })}>
                    <option value="none">Henüz seçilmedi</option>
                    <option value="manay">Manay CPA</option>
                    <option value="sky">Sky Accounting</option>
                  </select>
                </div>
              )}

              <ChecklistToggle
                label="Mercury Hesabı Açılacak mı?"
                value={onboarding.mercury_setup}
                onChange={v => setOnboarding({ ...onboarding, mercury_setup: v })}
              />

              <ChecklistToggle
                label="Teşvik Çalışılacak mı?"
                value={onboarding.incentive}
                onChange={v => setOnboarding({ ...onboarding, incentive: v })}
              />

              <p style={{ fontSize: 12, color: '#9A9A92', marginTop: 12 }}>
                <Icon name="info" size={14} /> Hesap oluşturulduktan sonra Drive folder ve Slack kanalı n8n tarafından otomatik kurulacak.
                Mailler hesap detayında "Mail Preview & Gönder" sekmesinden gönderilir.
              </p>

              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button type="submit" className="btn btn--primary" style={{ flex: 1 }} disabled={submitting}>
                  {submitting ? 'Oluşturuluyor...' : 'Hesabı Oluştur'}
                </button>
                <button type="button" className="btn btn--secondary" style={{ flex: 1 }} onClick={() => setShowForm(false)}>İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ChecklistToggle({ label, value, onChange }) {
  return (
    <div className="form-group" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
      <label className="form-label" style={{ margin: 0 }}>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" onClick={() => onChange(true)} style={{
          padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
          background: value ? '#34D399' : '#17191C',
          color: value ? '#0E0F11' : '#9A9A92', fontWeight: 600, fontSize: 13,
        }}>Evet</button>
        <button type="button" onClick={() => onChange(false)} style={{
          padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
          background: !value ? '#FF6B5B' : '#17191C',
          color: !value ? '#0E0F11' : '#9A9A92', fontWeight: 600, fontSize: 13,
        }}>Hayır</button>
      </div>
    </div>
  );
}
